import { mdiCheck, mdiClose, mdiDelete, mdiPencil, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import React from "react";
import { FunctionComponent, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";

import {
  defaultItem,
  OrderItemModelV2,
} from "../../../../../service/models/orders/order_item_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import ConsumablesSearchField from "../../components/consumables_search_field";

interface ExtraRowWidgetProps {
  extra?: OrderItemModelV2;
  onSubmit: (data: OrderItemModelV2) => void;
  edit: boolean;
  onDelete?: (i: OrderItemModelV2) => void;
  // form_id?: string;
  id_prefix?: string;
}

const ExtraRowWidget: FunctionComponent<ExtraRowWidgetProps> = (props) => {
  const { showToast } = useToast();

  const { edit, onSubmit } = props;
  const [extra, setExtra] = useState<OrderItemModelV2>({
    ...(props.extra ?? defaultItem),
  });
  const [editable, setEditable] = useState(false);

  const [warehouseSelected, setWarehouseSelected] = useState<boolean>(
    !!props.extra?.warehouse_id,
  );

  useEffect(() => {
    setEditable(edit);
    if (warehouseSelected === false) {
      setEditable(true);
    } else {
      setEditable(false);
    }
  }, [edit, warehouseSelected]);
  const onDone = () => {
    if (!extra.item_id || extra.qty < 1) {
      showToast({
        type: ToastType.warning,
        text: "Please select warehouse, item and qty",
      });
      return;
    }

    if (extra?.available_qty !== undefined) {
      if (extra.qty && extra.qty > extra.available_qty) {
        return showToast({
          type: ToastType.error,
          text: "Filled QTY is greater than Available QTY",
        });
      }
    } else {
    }

    if (!extra.item_id || extra.qty < 1 || !extra.warehouse_id) {
      showToast({
        type: ToastType.warning,
        text: "Please select warehouse, item and qty",
      });
      return;
    }
    onSubmit(extra);
  };

  // Stable, automation-friendly row id: existing rows key off the item code,
  // the trailing blank form row is always "new".
  const rowId = `${props.id_prefix ?? "order-create-consumable"}-row-${
    props.extra ? (props.extra.item?.item_code ?? props.extra.item_id) : "new"
  }`;

  return (
    <>
      <form
        id={rowId}
        onSubmit={(e) => {
          e.preventDefault();
          onDone();
        }}
        className="bg-white rounded-md shadow p-3 md:p-6 space-y-3 md:space-y-4 text-xs md:text-sm"
      >
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 md:gap-4 md:items-start">
          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Warehouse</span>
            <WarehouseSearchField
              id={`${rowId}-warehouse-search`}
              placeholder="Select Warehouse"
              value={extra.warehouse}
              onSelect={(data: WarehouseModel) => {
                console.log("Selected Warehouse ", data);
                setExtra((prev) => ({
                  ...prev,
                  warehouse_id: data.id,
                  warehouse: data,
                }));
                setWarehouseSelected(true);
              }}
            />
          </div>

          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Item Code</span>
            <input
              id={`${rowId}-item-code`}
              value={extra?.item?.item_code}
              disabled
              placeholder="Item Code"
              type="text"
              className="text-left text-xs md:text-sm p-2 rounded bg-gray-100 border border-gray-200 w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">Item</span>
            <ConsumablesSearchField
              id={`${rowId}-item-search`}
              warehouse_id={extra.warehouse_id ?? ""}
              disabled={editable}
              placeholder="Select Item"
              value={extra.item}
              onSelect={(item) => {
                setExtra((o) => ({
                  ...o,
                  item_id: item.id,
                  item,
                  available_qty: item.available_units ?? 0,
                }));
              }}
            />
          </div>

          <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
            <span className="md:hidden text-gray-400 text-xs">
              Item Description
            </span>
            <input
              id={`${rowId}-description`}
              value={extra?.item?.description}
              disabled
              placeholder="Item Description"
              type="text"
              className="text-left text-xs md:text-sm p-2 rounded bg-gray-100 border border-gray-200 w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">Item Unit</span>
            <input
              id={`${rowId}-unit`}
              value={extra?.item?.unit?.formal_name ?? "--"}
              disabled
              placeholder="Item Unit"
              type="text"
              className="text-left text-xs md:text-sm p-2 rounded bg-gray-100 border border-gray-200 w-full"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="md:hidden text-gray-400 text-xs">Quantity</span>
            <input
              id={`${rowId}-qty`}
              value={extra.qty || ""}
              onChange={(e) => {
                // const unitNo = Number(e.target.value) || 0;
                let v = 0;
                if (e.target.value && !isNaN(e.target.value as any)) {
                  v = Number(e.target.value);
                }

                if (extra.available_qty && extra.available_qty < v)
                  return showToast({
                    type: ToastType.error,
                    text: "Filled QTY is greated than AVAILABLE QTY",
                  });
                setExtra((ol) => ({ ...ol, qty: v }));
              }}
              type="number"
              className="text-left text-xs md:text-sm p-2 border rounded w-full focus:outline-none"
            />
            <span
              id={`${rowId}-available-qty`}
              className="text-xs text-gray-500 block"
            >
              Avail Units: {extra.available_qty}
            </span>
          </div>

          <div className="flex items-center gap-2 justify-center">
            {props.extra ? (
              <>
                {editable ? (
                  <>
                    <button
                      id={`${rowId}-save-btn`}
                      type="button"
                      onClick={() => {
                        if (!window.confirm("Are you sure?")) return;
                        onDone();
                        setEditable(false);
                      }}
                      className="bg-green-100 text-green-800 p-2 rounded hover:scale-110 transition-transform"
                    >
                      <Icon path={mdiCheck} className="h-4 w-4" />
                    </button>
                    <button
                      id={`${rowId}-cancel-btn`}
                      type="button"
                      onClick={() => {
                        setExtra(props.extra!);
                        setEditable(false);
                      }}
                      className="bg-red-100 text-red-800 p-2 rounded hover:scale-110 transition-transform"
                    >
                      <Icon path={mdiClose} className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    id={`${rowId}-edit-btn`}
                    type="button"
                    onClick={() => setEditable(true)}
                    className="bg-green-100 text-green-800 p-2 rounded hover:scale-110 transition-transform"
                  >
                    <Icon path={mdiPencil} className="h-4 w-4" />
                  </button>
                )}
                <button
                  id={`${rowId}-delete-btn`}
                  type="button"
                  onClick={() => {
                    if (!window.confirm("Are you sure?")) return;
                    props.onDelete?.(extra);
                  }}
                  className="bg-red-200 text-red-800 p-2 rounded hover:scale-110 transition-transform"
                >
                  <Icon path={mdiDelete} className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button
                id={`${rowId}-add-btn`}
                type="submit"
                className="ml-auto md:ml-0 flex items-center justify-center gap-1 text-white bg-myPrimaryColor rounded-full px-3 py-1 hover:scale-110 transition-transform"
              >
                <Icon path={mdiPlus} className="h-5 w-5" />
                <span className="hidden md:inline">Add</span>
              </button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default ExtraRowWidget;
